import { useEffect, useRef } from 'react';
import { throttle } from 'lodash-es';
import { usePageView, usePageScroll } from './AdobeAnalyticsHooks/useAdobeAnalytics';
import { useLocationInfo } from './AdobeAnalyticsHooks/useLocationInfo';
interface UsePageViewTrackerProps {
  pageIdentifier: string | undefined;
  market?: string;
  mainCategory?: string;
  categoryTree?: string;
}

export const usePageViewTracker = ({ pageIdentifier }: UsePageViewTrackerProps) => {
  const locationInfo = useLocationInfo();
  const { trackPageView } = usePageView();
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (pageIdentifier && !hasTrackedRef.current) {
      trackPageView({ pageIdentifier, locationInfo });
      hasTrackedRef.current = true;
    }
  }, [pageIdentifier, trackPageView, locationInfo]);
};

export const useScrollEventTracker = ({ pageIdentifier }: UsePageViewTrackerProps) => {
  const locationInfo = useLocationInfo();
  const { trackScrollEvent } = usePageScroll();
  const hasTriggeredRef = useRef(false);

  useEffect(() => {
    hasTriggeredRef.current = false;
  }, [pageIdentifier]);

  useEffect(() => {
    const handleScroll = () => {
      if (hasTriggeredRef.current) {
        return;
      }

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      if (scrollHeight <= 0) return;

      const scrollPercentage = Math.round((scrollTop / scrollHeight) * 100);

      if (pageIdentifier && scrollPercentage >= 75) {
        trackScrollEvent({
          pageIdentifier,
          scrollPercentage: 75,
          locationInfo,
        });
        hasTriggeredRef.current = true;
      }
    };
    const throttledHandleScroll = throttle(handleScroll, 100, {
      leading: true,
      trailing: true,
    });

    window.addEventListener('scroll', throttledHandleScroll);

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
      throttledHandleScroll.cancel();
    };
  }, [pageIdentifier, trackScrollEvent, locationInfo]);
};
